/**
 * ObjectFormModal — добавление/редактирование объекта (беседки).
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createObject, updateObject, fetchPayrollConfigs,
  OBJECT_STATUS_LABELS,
  type ObjectRow, type ObjectPayload, type ObjectStatus,
} from "@/api/finance";
import { ApiError } from "@/api/client";


interface Props {
  open: boolean;
  initial?: ObjectRow | null;
  onClose: () => void;
}


export function ObjectFormModal({ open, initial, onClose }: Props) {
  const isEdit = !!initial;
  const qc = useQueryClient();
  const [client, setClient] = useState("");
  const [region, setRegion] = useState<"" | "Москва" | "Юг">("");
  const [sqm, setSqm] = useState<string>("");
  const [contractSum, setContractSum] = useState<string>("");
  const [status, setStatus] = useState<ObjectStatus>("in_progress");
  const [periodLabel, setPeriodLabel] = useState("");
  const [isPromo, setIsPromo] = useState(false);
  const [comment, setComment] = useState("");
  const [contractSignedAt, setContractSignedAt] = useState<string>("");
  const [salesUserId, setSalesUserId] = useState<string>("");
  const [personalUserId, setPersonalUserId] = useState<string>("");
  const [personalPct, setPersonalPct] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Кандидаты на роль «менеджер ОП» — все из payroll (или с relevant role).
  const cfgQ = useQuery({
    queryKey: ["finance-payroll-configs"],
    queryFn: fetchPayrollConfigs,
    enabled: open,
    staleTime: 60_000,
  });
  const managers = useMemo(
    () => (cfgQ.data?.items ?? []).filter(u =>
      u.role === "op_manager" || u.role === "head_op" ||
      u.role === "owner" || u.is_superuser ||
      !!u.team_id),    // если в payroll-команде — тоже кандидат
    [cfgQ.data]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (initial) {
      setClient(initial.client_name);
      setRegion((initial.region as "" | "Москва" | "Юг") || "");
      setSqm(initial.sqm !== null ? String(initial.sqm) : "");
      setContractSum(initial.contract_sum ? String(initial.contract_sum) : "");
      setStatus(initial.status);
      setPeriodLabel(initial.period_label || "");
      setIsPromo(initial.is_promotion);
      setComment(initial.comment || "");
      setContractSignedAt(initial.contract_signed_at || "");
      setSalesUserId(initial.sales_user_id || "");
      setPersonalUserId(initial.payroll_personal_user_id || "");
      setPersonalPct(initial.payroll_personal_motivation_pct != null ? String(initial.payroll_personal_motivation_pct) : "");
    } else {
      setClient(""); setRegion(""); setSqm(""); setContractSum("");
      setStatus("in_progress"); setPeriodLabel(""); setIsPromo(false); setComment("");
      setContractSignedAt(""); setSalesUserId("");
      setPersonalUserId(""); setPersonalPct("");
    }
  }, [open, initial]);

  const mut = useMutation({
    mutationFn: async () => {
      if (!client.trim()) throw new Error("Имя клиента обязательно");
      const sqmN = sqm ? parseFloat(sqm.replace(",", ".")) : null;
      const sumN = contractSum ? parseFloat(contractSum.replace(",", ".")) : null;
      const payload: ObjectPayload = {
        client_name: client.trim(),
        region: region || null,
        sqm: sqmN,
        contract_sum: sumN,
        status,
        period_label: periodLabel.trim() || null,
        is_promotion: isPromo,
        comment: comment.trim() || null,
        contract_signed_at: contractSignedAt || null,
        sales_user_id: salesUserId || null,
        payroll_personal_user_id: personalUserId || null,
        payroll_personal_motivation_pct: personalPct ? parseFloat(personalPct.replace(",", ".")) : null,
      };
      return isEdit ? updateObject(initial!.id, payload) : createObject(payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["finance-objects"] });
      void qc.invalidateQueries({ queryKey: ["finance-overview"] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError
        ? ((e.body as { detail?: string })?.detail || `HTTP ${e.status}`)
        : (e as Error).message;
      setError(msg);
    },
  });

  if (!open) return null;
  return (
    <div
      className="modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
        display: "grid", placeItems: "center", zIndex: 100, padding: 16,
      }}
    >
      <div className="card" style={{ maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24, position: "relative" }}>
        <button
          type="button" onClick={onClose} aria-label="Закрыть"
          style={{ position: "absolute", top: 12, right: 12, border: "none", background: "transparent", color: "var(--mute)", cursor: "pointer", fontSize: 20, padding: 4 }}
        >×</button>

        <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700, marginBottom: 20, color: "var(--ink)" }}>
          {isEdit ? "Редактировать объект" : "Новый объект"}
        </h3>

        <form onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}
              style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={fieldStyle}>
            Клиент / название
            <input type="text" className="input" required value={client}
                   onChange={e => setClient(e.target.value)} placeholder="Иванов / Объект на ул. ..."/>
          </label>

          <div className="row row-2">
            <label style={fieldStyle}>
              Регион
              <select className="input" value={region} onChange={e => setRegion(e.target.value as "Москва" | "Юг" | "")}>
                <option value="">— не указан —</option>
                <option value="Москва">Москва</option>
                <option value="Юг">Юг</option>
              </select>
            </label>
            <label style={fieldStyle}>
              Период (как в экселе)
              <input type="text" className="input" value={periodLabel}
                     onChange={e => setPeriodLabel(e.target.value)} placeholder="Октябрь 2025"/>
            </label>
          </div>

          <div className="row row-2">
            <label style={fieldStyle}>
              Площадь, м²
              <input type="text" inputMode="decimal" className="input" value={sqm}
                     onChange={e => setSqm(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="50"/>
            </label>
            <label style={fieldStyle}>
              Сумма договора, ₽
              <input type="text" inputMode="decimal" className="input" value={contractSum}
                     onChange={e => setContractSum(e.target.value.replace(/[^\d.,]/g, ""))} placeholder="3500000"/>
            </label>
          </div>

          <div className="row row-2">
            <label style={fieldStyle}>
              Статус
              <select className="input" value={status} onChange={e => setStatus(e.target.value as ObjectStatus)}>
                {(Object.keys(OBJECT_STATUS_LABELS) as ObjectStatus[]).map(s => (
                  <option key={s} value={s}>{OBJECT_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </label>
            <label style={{ ...fieldStyle, alignSelf: "end", flexDirection: "row", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={isPromo} onChange={e => setIsPromo(e.target.checked)}/>
              <span>Договор по акции / со скидкой</span>
            </label>
          </div>

          <div className="row row-2">
            <label style={fieldStyle}>
              Дата подписания договора
              <input type="date" className="input" value={contractSignedAt}
                     onChange={e => setContractSignedAt(e.target.value)}/>
            </label>
            <label style={fieldStyle}>
              Менеджер ОП
              <select className="input" value={salesUserId}
                      onChange={e => setSalesUserId(e.target.value)}>
                <option value="">— автоматически (по имени из AmoCRM) —</option>
                {managers.map(m => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name || m.email}
                    {m.team_name ? ` · команда «${m.team_name}»` : ""}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
                Если задан — используется напрямую для расчёта ЗП (без matching по имени).
              </span>
            </label>
          </div>

          <div style={{ padding: 10, background: "rgba(247,224,138,0.20)", border: "1px solid rgba(216,170,56,0.4)", borderRadius: 6 }}>
            <div style={{ fontSize: 11, color: "#a85f12", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, fontWeight: 700 }}>
              Личный договор (опц.)
            </div>
            <div className="row row-2">
              <label style={fieldStyle}>
                «Личный менеджер» (100% мотивации)
                <select className="input" value={personalUserId}
                        onChange={e => setPersonalUserId(e.target.value)}>
                  <option value="">— не личный (общий пул команды) —</option>
                  {managers.map(m => (
                    <option key={m.user_id} value={m.user_id}>{m.display_name || m.email}</option>
                  ))}
                </select>
              </label>
              <label style={fieldStyle}>
                Личный % мотивации (опц.)
                <input className="input" type="number" step="0.01" value={personalPct}
                       onChange={e => setPersonalPct(e.target.value.replace(/[^\d.,]/g, ""))}
                       placeholder="напр. 5 = 5% от прихода"/>
              </label>
            </div>
            <span style={{ fontSize: 11, color: "var(--mute)" }}>
              Если задан — приходы по объекту целиком идут этому менеджеру, мотивация % — из этой ставки
              (или общий tier команды если не указано).
            </span>
          </div>

          <label style={fieldStyle}>
            Комментарий
            <textarea className="input" rows={2} value={comment}
                      onChange={e => setComment(e.target.value)}
                      style={{ resize: "vertical", fontFamily: "var(--sans)" }}/>
          </label>

          {error && (
            <div className="alert alert-red"><div className="alert-body"><div>{error}</div></div></div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={mut.isPending}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
              {mut.isPending ? "Сохранение…" : (isEdit ? "Сохранить" : "Добавить")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


const fieldStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ink-3)",
};
